import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Button, Chip, Collapse, CircularProgress,
    Divider, IconButton, InputAdornment, List, ListItem, TextField, Typography,
} from '@mui/material';
import { LocationOn, Search as SearchIcon, Clear } from '@mui/icons-material';
import { businessAPI } from '../api';

// Ensure the Google Maps script is loaded (safe to call multiple times)
function loadGoogleMapsScript() {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_PUBLIC_KEY;
    if (!apiKey) return;
    const scriptId = 'google-maps-places-script';
    if (document.getElementById(scriptId)) return;
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    document.head.appendChild(script);
}

function getGoogleServices() {
    return new Promise((resolve) => {
        const check = () => {
            if (window.google?.maps?.places?.AutocompleteService) {
                const svc = new window.google.maps.places.AutocompleteService();
                const placeSvc = new window.google.maps.places.PlacesService(document.createElement('div'));
                resolve({ svc, placeSvc });
            } else {
                setTimeout(check, 200);
            }
        };
        check();
    });
}

function parseGooglePlace(place) {
    const get = (type) =>
        (place.address_components || []).find(c => c.types.includes(type))?.long_name || '';
    return {
        place_id: place.place_id || '',
        name: place.name || '',
        display_name: place.name || '',
        lat: place.geometry?.location?.lat() || '',
        lng: place.geometry?.location?.lng() || '',
        city: get('locality') || get('administrative_area_level_2') || '',
        state: get('administrative_area_level_1') || '',
        pincode: get('postal_code') || '',
    };
}

export function parsePlace(r) {
    const a = r.address || {};
    return {
        place_id: r.osm_type && r.osm_id ? `${r.osm_type}:${r.osm_id}` : String(r.place_id),
        name: r.display_name.split(',')[0].trim(),
        display_name: r.display_name,
        lat: r.lat,
        lng: r.lon,
        city: a.city || a.town || a.village || a.county || '',
        state: a.state || '',
        pincode: a.postcode || '',
    };
}

const useGoogleMapsAvailable = () => !!process.env.REACT_APP_GOOGLE_MAPS_PUBLIC_KEY;

// GpsSelector — handles GPS place selection for an area form.
// Props:
//   existingName      — name of currently linked community (string or null)
//   picked            — selected place object (or null)
//   cleared           — true if user explicitly removed the existing link
//   onPick(place)     — called when user selects a place
//   onClear()         — called when user removes the newly picked place
//   onRemoveExisting()— called when user removes the pre-existing community link
const GpsSelector = ({ existingName, picked, cleared, onPick, onClear, onRemoveExisting }) => {
    const [expanded, setExpanded] = useState(false);
    const [search, setSearch] = useState('');
    const [dbResults, setDbResults] = useState([]);
    const [mapResults, setMapResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const debounceRef = useRef(null);
    const googleSvcRef = useRef(null);
    const hasGoogle = useGoogleMapsAvailable();

    // Load Google Maps script on mount
    useEffect(() => {
        if (hasGoogle) loadGoogleMapsScript();
    }, [hasGoogle]);

    // Init Google services when expanded
    useEffect(() => {
        if (!expanded || !hasGoogle) return;
        if (googleSvcRef.current) return;
        getGoogleServices().then(svcs => { googleSvcRef.current = svcs; });
    }, [expanded, hasGoogle]);

    useEffect(() => {
        const q = search.trim();
        if (!q || q.length < 2) { setDbResults([]); setMapResults([]); return; }
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setSearching(true);

            // Always search DB first
            const dbRes = await businessAPI.searchCommunities(q)
                .then(r => r.data.communities)
                .catch(() => []);

            const dbNames = new Set(dbRes.map(c => c.f_name.toLowerCase()));

            // Search Google Places or fall back to Nominatim
            let mapRes = [];
            if (hasGoogle && googleSvcRef.current) {
                mapRes = await new Promise((resolve) => {
                    googleSvcRef.current.svc.getPlacePredictions(
                        { input: q, componentRestrictions: { country: 'in' }, types: ['establishment', 'geocode'] },
                        (predictions, status) => {
                            const ok = window.google.maps.places.PlacesServiceStatus.OK;
                            resolve(status === ok ? predictions : []);
                        }
                    );
                });
                // Filter out names already in DB
                mapRes = mapRes.filter(p => !dbNames.has((p.structured_formatting?.main_text || p.description || '').toLowerCase()));
            } else {
                // Nominatim fallback if no Google key
                try {
                    const r = await fetch(
                        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${encodeURIComponent(q)}&limit=5&countrycodes=in`,
                        { headers: { 'Accept-Language': 'en' } }
                    );
                    const raw = await r.json();
                    mapRes = raw.map(parsePlace).filter(p => !dbNames.has(p.name.toLowerCase()));
                } catch { mapRes = []; }
            }

            setDbResults(dbRes);
            setMapResults(mapRes);
            setSearching(false);
        }, 350);
        return () => clearTimeout(debounceRef.current);
    }, [search, hasGoogle]);

    const handlePickGoogle = (prediction) => {
        if (!googleSvcRef.current) return;
        googleSvcRef.current.placeSvc.getDetails(
            { placeId: prediction.place_id, fields: ['name', 'geometry', 'address_components', 'place_id'] },
            (place, status) => {
                if (status !== window.google.maps.places.PlacesServiceStatus.OK) return;
                handlePick(parseGooglePlace(place));
            }
        );
    };

    const handlePick = (place) => {
        setExpanded(false);
        setSearch('');
        setDbResults([]);
        setMapResults([]);
        onPick(place);
    };

    if (picked) {
        return (
            <Chip
                icon={<LocationOn />}
                label={picked.name || picked.f_name}
                color="success"
                size="small"
                onDelete={onClear}
                sx={{ mt: 1 }}
            />
        );
    }

    if (existingName && !cleared) {
        return (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip icon={<LocationOn />} label={existingName} color="success" size="small"
                    onDelete={onRemoveExisting} />
                <Button size="small" variant="text" sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                    onClick={() => { onRemoveExisting(); setExpanded(true); }}>
                    Change
                </Button>
            </Box>
        );
    }

    const hasResults = dbResults.length > 0 || mapResults.length > 0;

    return (
        <Box sx={{ mt: 1 }}>
            {!expanded && (
                <Button size="small" variant="text" startIcon={<LocationOn fontSize="small" />}
                    onClick={() => setExpanded(true)}
                    sx={{ textTransform: 'none', color: 'text.secondary', pl: 0 }}>
                    Add GPS location (optional)
                </Button>
            )}
            <Collapse in={expanded}>
                <Box sx={{ mt: 1, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                            size="small" fullWidth autoFocus
                            placeholder="Type to search place, apartment…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        {searching
                                            ? <CircularProgress size={14} />
                                            : <SearchIcon fontSize="small" />}
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <IconButton size="small"
                            onClick={() => { setExpanded(false); setSearch(''); setDbResults([]); setMapResults([]); }}>
                            <Clear fontSize="small" />
                        </IconButton>
                    </Box>

                    {hasResults && (
                        <List dense disablePadding>
                            {dbResults.map((c) => (
                                <ListItem key={`db-${c.f_id}`} button
                                    onClick={() => handlePick({ f_id: c.f_id, name: c.f_name, f_name: c.f_name })}
                                    sx={{ px: 1, py: 0.5, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                                    <LocationOn fontSize="small" color="primary" sx={{ mr: 1, flexShrink: 0 }} />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={500} noWrap>{c.f_name}</Typography>
                                        {c.f_city && (
                                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                {c.f_city}{c.f_state ? `, ${c.f_state}` : ''}
                                            </Typography>
                                        )}
                                    </Box>
                                </ListItem>
                            ))}

                            {dbResults.length > 0 && mapResults.length > 0 && (
                                <Divider sx={{ my: 0.5 }}>
                                    <Typography variant="caption" color="text.disabled">More from maps</Typography>
                                </Divider>
                            )}

                            {mapResults.map((place, i) => {
                                const isGoogle = !!place.place_id && !place.lat;
                                const label = isGoogle
                                    ? place.structured_formatting?.main_text || place.description
                                    : place.display_name;
                                const sub = isGoogle
                                    ? place.structured_formatting?.secondary_text
                                    : null;
                                return (
                                    <ListItem key={`map-${i}`} button
                                        onClick={() => isGoogle ? handlePickGoogle(place) : handlePick(place)}
                                        sx={{ px: 1, py: 0.5, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                                        <LocationOn fontSize="small" color="action" sx={{ mr: 1, flexShrink: 0 }} />
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" noWrap>{label}</Typography>
                                            {sub && (
                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                    {sub}
                                                </Typography>
                                            )}
                                        </Box>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
};

export default GpsSelector;
